import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, Mail, CalendarDays, Target } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "@/components/SectionHeading";
import { BuyButton } from "@/components/BuyButton";
import ProtectedContent from "@/components/study/ProtectedContent";
import { Input } from "@/components/ui/input";
import { submitDiagnosticLead, getDiagnosticPlanPreview, trackEvent } from "@/lib/api";
import { trackGoogleEvent } from "@/lib/googleTracking";
import { STORE } from "@/constants/testIds";

const DISCLAIMER =
  "Illinois Broker Prep is an independent study aid. Not affiliated with PSI, IDFPR, the State of Illinois, or any real estate commission. No actual exam questions. No pass guarantee.";

const Container = ({ children }) => (
  <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">{children}</div>
);

const SECTIONS = [
  { key: "state", label: "Illinois State" },
  { key: "national", label: "National" },
  { key: "math", label: "Math" },
];

function loadResult(location) {
  if (location.state && location.state.result) return location.state.result;
  try {
    const raw = sessionStorage.getItem("ibp_diag_result");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export default function DiagnosticResults() {
  const location = useLocation();
  const result = useMemo(() => loadResult(location), [location]);

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [planPreview, setPlanPreview] = useState(null);
  const [emailConsent, setEmailConsent] = useState(false);

  useEffect(() => {
    if (result) {
      trackEvent("diagnostic_results_view", {
        correct: result.correct,
        total: result.total,
      });
      // New funnel event (kept alongside the legacy one for continuity)
      trackEvent("diagnostic_result_viewed", {
        correct: result.correct,
        total: result.total,
      });
      trackGoogleEvent("diagnostic_result_viewed", {
        correct: result.correct,
        total: result.total,
      });
    }
  }, [result]);

  const resultAttemptId = result?.attempt_id;

  // Load a grounded preview of the recommended study plan (conversion teaser)
  useEffect(() => {
    let active = true;
    if (!resultAttemptId) return undefined;
    getDiagnosticPlanPreview(resultAttemptId)
      .then((data) => {
        if (active) setPlanPreview(data);
      })
      .catch(() => {
        if (active) setPlanPreview(null);
      });
    return () => {
      active = false;
    };
  }, [resultAttemptId]);

  if (!result) {
    return (
      <div className="bg-[var(--ibp-offwhite)]">
        <section className="py-20">
          <Container>
            <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-8 text-center">
              <h1 className="text-xl font-semibold text-[var(--ibp-ink)]">
                No results yet
              </h1>
              <p className="mt-2 text-sm text-[color:rgba(11,18,32,0.65)]">
                Take the free diagnostic to see your score and weak areas.
              </p>
              <Link
                to="/diagnostic"
                className="mt-5 inline-flex items-center justify-center rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[var(--ibp-primary-hover)]"
              >
                Start the diagnostic
              </Link>
            </div>
          </Container>
        </section>
      </div>
    );
  }

  const { correct, total, by_section: bySection = {}, weak_areas: weak = [], recommendation, review = [], attempt_id: attemptId } = result;

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!emailConsent) {
      toast.error("Please confirm email consent", {
        description: "Check the box so we can email your results and study updates.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitDiagnosticLead(email, attemptId);
      setEmailStatus(res.email_status);
      setUnlocked(true);
      trackEvent("diagnostic_email_submitted");
      trackGoogleEvent("generate_lead", {
        method: "diagnostic_results",
        email_status: res.email_status,
      });
      toast.success("Results saved", {
        description: "Your full results are shown below.",
      });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={STORE.diagnosticResultsPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-14">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Your diagnostic results"
            title={`You scored ${correct} out of ${total}`}
            subtitle={recommendation}
          />
        </Container>
      </section>

      <section className="py-12 sm:py-16">
        <Container>
          {/* Section breakdown (visible immediately) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {SECTIONS.map(({ key, label }) => {
              const s = bySection[key] || { correct: 0, total: 0 };
              return (
                <div
                  key={key}
                  className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 text-center shadow-[0_10px_30px_rgba(7,20,39,0.05)]"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
                    {label}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-[var(--ibp-ink)]">
                    {s.correct}/{s.total}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Weak-topic checklist (visible immediately) */}
          <div
            data-testid={STORE.diagnosticWeakChecklist}
            className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.05)]"
          >
            <h2 className="text-base font-semibold text-[var(--ibp-ink)]">
              Your weak-area checklist
            </h2>
            <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
              These are the topics that cost you the most points. Knock them out first.
            </p>
            <ul className="mt-4 space-y-2.5">
              {weak.length === 0 && (
                <li className="text-sm text-[color:rgba(11,18,32,0.7)]">
                  No clear weak areas. Use the full online study platform to build speed and accuracy.
                </li>
              )}
              {weak.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-3 rounded-xl border border-[color:rgba(10,27,51,0.1)] bg-[var(--ibp-offwhite)] px-3.5 py-2.5 text-sm font-medium text-[var(--ibp-ink)]"
                >
                  <Target className="mt-0.5 h-4 w-4 flex-none text-[var(--ibp-gold-500)]" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Study-plan preview (grounded in the diagnostic weak areas) */}
          {planPreview && (planPreview.preview_days || []).length > 0 && (
            <div
              data-testid={STORE.diagnosticPlanPreview}
              className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.05)] sm:p-7"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]">
                  <CalendarDays className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--ibp-ink)]">
                    Your recommended {planPreview.label}
                  </h2>
                  <p className="text-sm text-[color:rgba(11,18,32,0.6)]">
                    Here are the first few days, built around your weak areas. The full{" "}
                    {planPreview.total_days}-day plan unlocks inside the online study platform.
                  </p>
                </div>
              </div>
              <div className="mt-5 space-y-3">
                {planPreview.preview_days.map((d) => (
                  <div
                    key={d.day}
                    data-testid={STORE.diagnosticPlanPreviewDay}
                    className="rounded-xl border border-[color:rgba(10,27,51,0.1)] bg-[var(--ibp-offwhite)] p-4"
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 min-w-6 items-center justify-center whitespace-nowrap rounded-md bg-[var(--ibp-navy-950)] px-2 text-xs font-bold text-white">
                        Day {d.day}
                      </span>
                      <p className="text-sm font-semibold text-[var(--ibp-ink)]">{d.title}</p>
                    </div>
                    <ul className="mt-2 space-y-1">
                      {(d.tasks || []).map((t, ti) => (
                        <li
                          key={ti}
                          className="flex items-start gap-2 text-sm text-[color:rgba(11,18,32,0.78)]"
                        >
                          <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-[var(--ibp-gold-500)]" />
                          <span>{t.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-center text-sm font-medium text-[color:rgba(11,18,32,0.7)]">
                + {Math.max(planPreview.total_days - planPreview.preview_days.length, 0)} more days
                of targeted practice, timed sprints, and missed-question review.
              </p>
            </div>
          )}

          {/* Primary CTA — strong, always visible conversion moment */}
          <div
            data-testid={STORE.diagnosticPrimaryCta}
            className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-gradient-to-br from-[var(--ibp-navy-950)] to-[#0a1b33] p-6 text-center text-white shadow-[0_18px_40px_rgba(7,20,39,0.18)] sm:p-8"
          >
            <h2 className="text-xl font-bold">Turn these weak areas into your strengths</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-[color:rgba(255,255,255,0.78)]">
              Get the full Illinois Broker Prep platform — focused Illinois state, national, and math practice,
              your complete study plan, timed sprints, and full explanations.
            </p>
            <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <BuyButton
                testId={STORE.diagnosticCheckoutCta}
                onClick={() => {
                  trackEvent("diagnostic_cta_clicked", { location: "results_primary" });
                  trackEvent("diagnostic_checkout_click");
                }}
              />
              <Link
                to={`/study/login${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                className="inline-flex min-h-11 items-center justify-center rounded-xl border border-[color:rgba(255,255,255,0.25)] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[color:rgba(255,255,255,0.08)]"
              >
                Start a free study account
              </Link>
            </div>
          </div>

          {/* Email capture gate */}
          {!unlocked && (
            <div
              data-testid={STORE.diagnosticEmailCard}
              className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-8"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--ibp-ink)]">
                    Send me my results and unlock the full review
                  </h2>
                  <p className="text-sm text-[color:rgba(11,18,32,0.6)]">
                    Enter your email to unlock the full question-by-question review and a study plan.
                  </p>
                </div>
              </div>
              <form onSubmit={handleEmail} className="mt-5 space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Input
                    type="email"
                    required
                    data-testid={STORE.diagnosticEmailInput}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="h-11 flex-1 rounded-xl"
                  />
                  <button
                    type="submit"
                    data-testid={STORE.diagnosticEmailSubmit}
                    disabled={submitting}
                    className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.2)] transition-colors hover:bg-[var(--ibp-primary-hover)] disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : "Email my results"}
                  </button>
                </div>
                <label className="flex items-start gap-2 text-xs leading-5 text-[color:rgba(11,18,32,0.68)]">
                  <input
                    type="checkbox"
                    checked={emailConsent}
                    onChange={(e) => setEmailConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-[color:rgba(10,27,51,0.25)]"
                  />
                  <span>
                    Email me my diagnostic results and occasional Illinois
                    Broker Prep study updates. I can unsubscribe anytime.
                  </span>
                </label>
              </form>
            </div>
          )}

          {/* Unlocked: email status + full review + CTA */}
          {unlocked && (
            <>
              <div
                data-testid={STORE.diagnosticEmailStatus}
                className="mt-6 rounded-xl border border-[color:rgba(10,27,51,0.12)] bg-white px-4 py-3 text-sm text-[var(--ibp-ink)]"
              >
                {emailStatus === "sent"
                  ? "We emailed your results and a study plan. Check your inbox."
                  : "We saved your results. Email delivery is being set up, so your full results are shown right here."}
              </div>

              <ProtectedContent className="mt-6 space-y-4">
                <h2 className="text-base font-semibold text-[var(--ibp-ink)]">
                  Review every question
                </h2>
                {review.map((q, i) => (
                  <article
                    key={q.id}
                    data-testid={STORE.diagnosticReviewItem}
                    className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 shadow-[0_10px_30px_rgba(7,20,39,0.05)]"
                  >
                    <div className="flex items-start gap-2">
                      {q.is_correct ? (
                        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#15803d]" />
                      ) : (
                        <XCircle className="mt-0.5 h-5 w-5 flex-none text-[#9b2c2c]" />
                      )}
                      <p className="text-sm font-semibold text-[var(--ibp-ink)]">
                        {i + 1}. {q.question_text}
                      </p>
                    </div>
                    <div className="mt-3 space-y-1.5 pl-7">
                      {["A", "B", "C", "D"].map((letter) => {
                        const isCorrect = q.correct_answer === letter;
                        const isYours = q.selected_answer === letter;
                        return (
                          <div
                            key={letter}
                            className={[
                              "rounded-lg px-3 py-2 text-sm",
                              isCorrect
                                ? "bg-[color:rgba(21,128,61,0.10)] font-semibold text-[#14532d]"
                                : isYours
                                ? "bg-[color:rgba(155,44,44,0.08)] text-[#7f1d1d]"
                                : "text-[color:rgba(11,18,32,0.75)]",
                            ].join(" ")}
                          >
                            <span className="font-bold">{letter}.</span> {q.choices[letter]}
                            {isCorrect ? "  (correct answer)" : isYours ? "  (your answer)" : ""}
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-3 pl-7 text-sm leading-6 text-[color:rgba(11,18,32,0.8)]">
                      {q.explanation}
                    </p>
                    {q.source_reference ? (
                      <p className="mt-2 pl-7 text-xs text-[color:rgba(11,18,32,0.55)]">
                        Source: {q.source_reference}
                      </p>
                    ) : null}
                  </article>
                ))}
              </ProtectedContent>

              <div className="mt-8 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-[var(--ibp-navy-950)] p-6 text-center text-white sm:p-8">
                <h2 className="text-lg font-semibold">
                  Ready to close the gaps?
                </h2>
                <p className="mx-auto mt-1.5 max-w-md text-sm text-[color:rgba(255,255,255,0.75)]">
                  The full Illinois Broker Prep platform gives you focused Illinois state, national, and math practice with explanations.
                </p>
                <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <BuyButton
                    onClick={() => {
                      trackEvent("diagnostic_cta_clicked", { location: "results_unlocked" });
                      trackEvent("diagnostic_checkout_click");
                    }}
                  />
                  <Link
                    to="/preview"
                    className="inline-flex min-h-11 items-center justify-center rounded-xl border border-[color:rgba(255,255,255,0.25)] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[color:rgba(255,255,255,0.08)]"
                  >
                    {"Preview What's Inside"}
                  </Link>
                </div>
              </div>
            </>
          )}

          <p className="mx-auto mt-8 max-w-2xl text-center text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
            {DISCLAIMER}
          </p>
        </Container>
      </section>
    </div>
  );
}
